import {
  ActionIcon,
  Box,
  Group,
  Image,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  rem,
  Modal,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE, FileWithPath } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { UploadFiles } from "../../hooks/usePerformRequest";

interface SampleImageUploaderProps {
  value: (string | File)[];
  onChange: (value: (string | File)[]) => void;
  error?: string;
}

export const SampleImageUploader = ({
  value,
  onChange,
  error,
}: SampleImageUploaderProps) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const handleDrop = (files: FileWithPath[]) => {
    if (files.length === 0) return;
    const next = [...value, ...files];
    onChange(next);
  };

  const removeImage = (index: number) => {
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  const getPreviewUrl = (item: string | File) => {
    if (typeof item === "string") return item;
    return URL.createObjectURL(item);
  };

  return (
    <Stack gap="sm">
      <Dropzone
        onDrop={handleDrop}
        onReject={(files) => {
          notifications.show({
            title: "File rejected",
            message: files[0].errors[0].message,
            color: "red",
          });
        }}
        maxSize={5 * 1024 ** 2}
        accept={IMAGE_MIME_TYPE}
        radius="md"
        styles={() => ({
          root: {
            border: `${rem(2)} dashed ${
              error
                ? "var(--mantine-color-red-6)"
                : "var(--mantine-color-gray-4)"
            }`,
            backgroundColor: "var(--mantine-color-gray-0)",
            "&[data-drag-over]": {
              backgroundColor: "var(--mantine-color-brand-0)",
              borderColor: "var(--mantine-color-brand-6)",
            },
            transition: "all 0.2s ease",
            cursor: "pointer",
          },
        })}
      >
        <Group
          justify="center"
          gap="xl"
          mih={160}
          style={{ pointerEvents: "none" }}
        >
          <Dropzone.Accept>
            <IconUpload
              style={{
                width: rem(52),
                height: rem(52),
                color: "var(--mantine-color-brand-6)",
              }}
              stroke={1.5}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX
              style={{
                width: rem(52),
                height: rem(52),
                color: "var(--mantine-color-red-6)",
              }}
              stroke={1.5}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto
              style={{
                width: rem(52),
                height: rem(52),
                color: "var(--mantine-color-dimmed)",
              }}
              stroke={1.5}
            />
          </Dropzone.Idle>

          <div>
            <Text size="xl" inline fw={700}>
              Drag images here or click to select
            </Text>
            <Text size="sm" c="dimmed" inline mt={7}>
              Upload sample proof images. Max 5MB per file.
            </Text>
          </div>
        </Group>
      </Dropzone>

      {error && (
        <Text size="xs" c="red" mt={-4}>
          {error}
        </Text>
      )}

      {value.length > 0 && (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="md" mt="sm">
          {value.map((item, index) => (
            <Paper
              key={index}
              radius="md"
              withBorder
              style={{
                overflow: "hidden",
                position: "relative",
                aspectRatio: "1/1",
              }}
              className="sample-image-preview"
            >
              <Image
                src={getPreviewUrl(item)}
                h="100%"
                w="100%"
                fit="cover"
                onClick={() => setPreviewImage(getPreviewUrl(item))}
                style={{ cursor: "pointer" }}
              />
              <Box
                style={{
                  position: "absolute",
                  top: rem(4),
                  right: rem(4),
                  zIndex: 10,
                }}
              >
                <ActionIcon
                  color="red"
                  variant="filled"
                  size="sm"
                  radius="xl"
                  onClick={() => removeImage(index)}
                  style={{
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  <IconX size={rem(14)} />
                </ActionIcon>
              </Box>
              {typeof item !== "string" && (
                <Box
                  style={{
                    position: "absolute",
                    bottom: rem(4),
                    left: rem(4),
                    zIndex: 10,
                  }}
                >
                  <Text size="10px" fw={700} c="white" bg="rgba(0,0,0,0.5)" px={4} style={{ borderRadius: 4 }}>
                    PENDING
                  </Text>
                </Box>
              )}
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Modal
        opened={!!previewImage}
        onClose={() => setPreviewImage(null)}
        title="Image Preview"
        centered
        size="lg"
      >
        {previewImage && (
          <Image
            src={previewImage}
            w="100%"
            h="auto"
            fit="contain"
            radius="md"
          />
        )}
      </Modal>
    </Stack>
  );
};
